import { Badge, Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { NavigateBefore, NavigateNext, Save } from '@mui/icons-material';
import type { ReactNode } from 'react';

interface TestFooterActionsProps {
  onSave: () => void;
  onNextSample: () => void;
  onPreviousSample?: () => void;
  onCancel: () => void;
  saving: boolean;
  /** Shows the warning-dot badge on the Save button when there are unsaved changes (mirrors TestActionBar). */
  dirty?: boolean;
  hasNext: boolean;
  hasPrevious?: boolean;
  hideSave?: boolean;
  /** Optional "current / total" pager chip, rendered after Next Sample to match the header pill. */
  pagerLabel?: ReactNode;
}

const BUTTON_HEIGHT = 38;

/**
 * Sticky footer action row for every Test workflow page. Each button is a
 * standalone pill that uses the same visual recipe as the header
 * `TestActionBar` segments (999px radius, hairline border, gradient
 * surface, tint overlay per role) so the top and bottom of the page read
 * as one design system.
 *
 * Why not a single segmented pill like the header: the bottom row mixes
 * destructive (Cancel), persisting (Save) and navigation (Previous / Next)
 * actions — keeping them as discrete buttons makes the affordance clearer
 * than fusing them together.
 */
export const TestFooterActions = ({
  onSave,
  onNextSample,
  onPreviousSample,
  onCancel,
  saving,
  dirty = false,
  hasNext,
  hasPrevious = false,
  hideSave = false,
  pagerLabel,
}: TestFooterActionsProps) => {
  const theme = useTheme();

  // Shared "mini-pill" recipe — identical to the header pill container so
  // every footer button looks like a single-segment version of the bar.
  const basePill = {
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: '0.82rem',
    letterSpacing: '0.01em',
    height: BUTTON_HEIGHT,
    minWidth: 'auto',
    px: 1.75,
    borderRadius: 999,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
    background: `linear-gradient(135deg,
      ${alpha(theme.palette.primary.main, 0.05)} 0%,
      ${alpha('#FFFFFF', 0.7)} 48%,
      ${alpha(theme.customColors.maroon, 0.035)} 100%)`,
    boxShadow: `0 1px 3px ${alpha('#000', 0.04)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.75)}`,
    transition: 'background-color 0.16s ease, color 0.16s ease, box-shadow 0.2s ease, border-color 0.16s ease',
    '&.Mui-disabled': {
      borderColor: alpha(theme.palette.primary.main, 0.1),
      color: alpha(theme.palette.text.primary, 0.35),
    },
  };

  // Per-segment tint overlays, mirroring TestActionBar exactly.
  const ghostSx = {
    ...basePill,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.text.primary,
      borderColor: alpha(theme.palette.primary.main, 0.28),
    },
    '&.Mui-disabled': {
      ...basePill['&.Mui-disabled'],
    },
  };

  const saveSx = {
    ...basePill,
    color: theme.customColors.maroon,
    borderColor: alpha(theme.customColors.maroon, 0.22),
    // Layer the maroon tint over the shared gradient so it still feels
    // like part of the same family.
    backgroundColor: alpha(theme.customColors.maroon, 0.08),
    background: `linear-gradient(135deg,
      ${alpha(theme.customColors.maroon, 0.1)} 0%,
      ${alpha(theme.customColors.maroon, 0.06)} 100%),
      linear-gradient(135deg,
        ${alpha(theme.palette.primary.main, 0.05)} 0%,
        ${alpha('#FFFFFF', 0.7)} 48%,
        ${alpha(theme.customColors.maroon, 0.035)} 100%)`,
    '&:hover': {
      backgroundColor: alpha(theme.customColors.maroon, 0.16),
      borderColor: alpha(theme.customColors.maroon, 0.32),
    },
  };

  const nextSx = {
    ...basePill,
    color: theme.palette.primary.dark,
    borderColor: alpha(theme.palette.primary.main, 0.28),
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    background: `linear-gradient(135deg,
      ${alpha(theme.palette.primary.main, 0.14)} 0%,
      ${alpha(theme.palette.primary.main, 0.08)} 100%),
      linear-gradient(135deg,
        ${alpha(theme.palette.primary.main, 0.05)} 0%,
        ${alpha('#FFFFFF', 0.7)} 48%,
        ${alpha(theme.customColors.maroon, 0.035)} 100%)`,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
      borderColor: alpha(theme.palette.primary.main, 0.4),
      // Same soft outer glow as the header Generate segment.
      boxShadow: `0 1px 3px ${alpha('#000', 0.04)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.75)}, 0 0 16px -2px ${alpha(theme.palette.primary.main, 0.45)}`,
    },
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1.25,
        py: 1.5,
        px: 2.5,
        mt: 1,
        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        background: `linear-gradient(180deg,
          ${alpha(theme.palette.background.default, 0.85)} 0%,
          ${alpha(theme.palette.background.default, 0.96)} 100%)`,
        backdropFilter: 'blur(8px)',
        zIndex: 10,
      }}
    >
      {/* ── Left cluster ─────────────────────────────────────────────── */}
      <Tooltip title={hasPrevious ? '' : 'First sample in work order'}>
        <span>
          <Button
            startIcon={<NavigateBefore sx={{ fontSize: 17 }} />}
            onClick={onPreviousSample}
            disabled={!hasPrevious || saving}
            sx={ghostSx}
          >
            Previous Sample
          </Button>
        </span>
      </Tooltip>

      {/* ── Right cluster: Cancel / Save / Next Sample / pager ───────── */}
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Button
          onClick={onCancel}
          sx={ghostSx}
        >
          Cancel
        </Button>

        {!hideSave && (
          <Tooltip title={dirty ? 'Save (unsaved changes)' : 'Save'}>
            <Badge
              variant="dot"
              invisible={!dirty || saving}
              color="warning"
              overlap="circular"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{
                '& .MuiBadge-dot': {
                  width: 10,
                  height: 10,
                  minWidth: 10,
                  borderRadius: '50%',
                  border: `2px solid ${theme.palette.background.paper}`,
                  boxShadow: `0 0 0 1px ${alpha(theme.customColors.maroon, 0.55)}`,
                  top: 7,
                  right: 7,
                },
                display: 'inline-flex',
              }}
            >
              <Button
                startIcon={saving
                  ? <CircularProgress size={15} sx={{ color: theme.customColors.maroon }} />
                  : <Save sx={{ fontSize: 17 }} />}
                onClick={onSave}
                disabled={saving}
                sx={saveSx}
              >
                {saving ? 'Saving' : 'Save'}
              </Button>
            </Badge>
          </Tooltip>
        )}

        <Tooltip title={hasNext ? '' : 'Last sample in work order'}>
          <span>
            <Button
              endIcon={<NavigateNext sx={{ fontSize: 17 }} />}
              onClick={onNextSample}
              disabled={!hasNext || saving}
              sx={nextSx}
            >
              Next Sample
            </Button>
          </span>
        </Tooltip>

        {pagerLabel && (
          <Box
            sx={{
              height: 26,
              minWidth: 46,
              px: 1,
              ml: 0.25,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 999,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              color: theme.palette.text.secondary,
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
            }}
          >
            <Typography variant="caption" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
              {pagerLabel}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
